import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Star, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [req, setReq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const bottomRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/requests/${id}/messages`); setReq(data.request); setMessages(data.messages); }
    catch { toast.error("Conversazione non trovata"); navigate("/requests"); }
  };
  useEffect(() => { load(); const i = setInterval(load, 12000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post(`/requests/${id}/messages`, { text: t }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const isOwner = req && req.owner_id === user?.user_id;
  const otherId = req ? (isOwner ? req.requester_id : req.owner_id) : null;

  const [pickupCode, setPickupCode] = useState("");
  const confirmPickup = async () => {
    try { await api.post(`/requests/${id}/confirm-pickup`, { code: pickupCode }); toast.success("Ritiro confermato! Hai guadagnato crediti."); await refresh(); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const noShow = async () => {
    try { await api.post(`/requests/${id}/no-show`); toast.success("Mancato ritiro segnalato."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const leaveReview = async () => {
    try {
      const src = isOwner ? await api.get("/requests/received") : await api.get("/requests/sent");
      const found = src.data.find((r) => r.id === id);
      await api.post("/reviews", { reviewee_id: otherId, listing_id: found.listing_id, rating, comment });
      toast.success("Recensione inviata!");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!req) return null;

  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-13rem)]">
      <div className="flex items-center gap-3 pb-4 border-b border-border/60">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate("/requests")} data-testid="chat-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="font-head font-bold text-lg">{req.listing_title}</div>
          <Badge variant="outline" className="text-xs mt-0.5">{req.status === "accepted" ? "Accettata" : req.status === "rejected" ? "Rifiutata" : "In attesa"}</Badge>
        </div>
        {isOwner && req.status === "accepted" && (
          <div className="flex items-center gap-1">
            <Input value={pickupCode} onChange={(e) => setPickupCode(e.target.value)} placeholder="Codice" className="h-9 w-24 rounded-full" data-testid="pickup-code-input" />
            <Button size="sm" className="rounded-full bg-success hover:bg-success/90" onClick={confirmPickup} data-testid="confirm-pickup-btn"><PackageCheck className="h-4 w-4 mr-1" /> Conferma</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={noShow} data-testid="no-show-btn">Mancato ritiro</Button>
          </div>
        )}
        <Dialog>
          <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid="review-btn"><Star className="h-4 w-4 mr-1" /> Recensisci</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Lascia una recensione</DialogTitle></DialogHeader>
            <div className="flex gap-1 justify-center py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <button key={i} onClick={() => setRating(i + 1)} data-testid={`star-${i + 1}`}><Star className={`h-8 w-8 ${i < rating ? "text-[hsl(var(--gold))] fill-[hsl(var(--gold))]" : "text-border"}`} /></button>
              ))}
            </div>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Com'è andato lo scambio?" className="rounded-xl" data-testid="review-comment" />
            <DialogFooter><Button onClick={leaveReview} className="rounded-full" data-testid="submit-review-btn">Invia recensione</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!isOwner && req.status === "accepted" && req.pickup_code && (
        <div className="mt-3 rounded-xl bg-success/10 border border-success/30 px-4 py-2 text-sm" data-testid="pickup-code-banner">
          Il tuo codice ritiro: <b className="font-mono tracking-widest text-base">{req.pickup_code}</b> — mostralo al donatore al momento del ritiro.
        </div>
      )}

      <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="chat-messages">
        {messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>{m.text}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="flex gap-2 pt-3 border-t border-border/60">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio..." className="h-11 rounded-full" data-testid="chat-input" />
        <Button type="submit" size="icon" className="rounded-full h-11 w-11" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
